import { Accordion, AccordionTab } from 'primereact/accordion';
import { confirmPopup, ConfirmPopup } from 'primereact/confirmpopup';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { QueryClient, useMutation } from 'react-query';
import { useWindowSize } from 'react-use';
import MlxActionIcon from '../../../../components/common/MlxActionIcon/MlxActionIcon';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import { AlertVariant, MOBILE_BREAKPOINT, OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../constants/constants';
import { ORG_ADMIN_PERMISSION_GROUP_LIST } from '../../../../constants/queryKeys';
import { _Permission, _PermissionGroup, _ToastMessageProps } from '../../../../constants/staticTypes';
import {
    ADD,
    CANCEL,
    CUSTOM_PERMISSIONS,
    DEFAULT_PERMISSIONS,
    DELETE_PERMISSION_MSG,
    PERMISSION_DESC,
    PERMISSION_NAME,
    SAVE,
} from '../../../../constants/strings';
import { AuthCtx } from '../../../../context/AuthCtxProvider';
import { defaultToastData } from '../../../../helpers/commonHelper';
import HttpServiceHelper from '../../../../helpers/httpServiceHelper';
import { JTranslation } from '../../../../helpers/jTranslationHelper';
import { deleteOrgPermission, getOrgPermissionWithGroupList, updateOrgPermission } from '../../../../helpers/orgPermissionHelper';
import {
    _RolePermissions,
    _UpdatePermissionForm,
    convertToRolePermissions,
    onPermissionChange,
    updateOrgFullPermissions,
} from '../../../../helpers/permissionHelper';
import withSidebar from '../../../../hoc/withSideBar/withSideBar';
import { useQueryHook } from '../../../../hooks/useQueryHook';
import { useTranslation } from '../../../../hooks/useTranslation';
import logger from '../../../../utils/logger';
import AddOrgAdminPermission from './components/AddOrgAdminPermission';
import { checkActionSubPermission } from '../../../../utils/common';

const languageTexts = {
    DEFAULT_PERMISSIONS: DEFAULT_PERMISSIONS,
    CUSTOM_PERMISSIONS: CUSTOM_PERMISSIONS,
    ADD: ADD,
    PERMISSION_NAME: PERMISSION_NAME,
    PERMISSION_DESC: PERMISSION_DESC,
    CANCEL: CANCEL,
    SAVE: SAVE,
};

function OrgStaffPermission() {
    const queryClient = new QueryClient();
    const deletePermissionMutation = useMutation(HttpServiceHelper);
    const updatePermissionMutation = useMutation(HttpServiceHelper);
    const { width: screenWidth } = useWindowSize();
    const translatedText = useTranslation(languageTexts, 'pascal');
    const { userType } = useContext(AuthCtx);

    const [adminOrgPermissionList, setAdminOrgPermissionList] = useState<_Permission[]>([] as _Permission[]);
    const [adminGroupPermissionList, setAdminGroupPermissionList] = useState<_PermissionGroup[]>([] as _PermissionGroup[]);
    const [activeGroupPermission, setActiveGroupPermission] = useState<_PermissionGroup | null>(null);
    const [activeGroupPermissionList, setActiveGroupPermissionList] = useState<_Permission[] | null>(null);
    const [updatePermissionForm, setUpdatePermissionForm] = useState<_UpdatePermissionForm | null>(null);
    const [addAdminOrgPermissionModel, setAddAdminOrgPermissionModel] = useState<boolean>(false);
    const [resetKey, setResetKey] = useState(0);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);

    // get admin permission and admin group permission list
    const orgPermissionGroupListQuery = useQueryHook({
        queryKey: ORG_ADMIN_PERMISSION_GROUP_LIST,
        queryFn: async () => await getOrgPermissionWithGroupList(queryClient),
        onSuccess: (response: any) => {
            const { adminOrgPermissionListData, adminOrgPermissionGroupListData } = response;

            setAdminGroupPermissionList(adminOrgPermissionGroupListData);
            setAdminOrgPermissionList(adminOrgPermissionListData);

            // set first permission as default, if no permission selected
            if (!activeGroupPermission && adminOrgPermissionGroupListData && adminOrgPermissionGroupListData.length > 0) {
                setActiveGroupPermission(adminOrgPermissionGroupListData[0]);
            }
        },
        onError: (error) => logger.log('Failed to fetch permission list', error),
        refetchOnWindowFocus: false,
        enabled: true,
        enableLoader: true,
    });

    // on permission change
    const onPermissionChangeEvent = (event: ChangeEvent<HTMLInputElement>, permission: _Permission) => {
        const updatedPermissions = onPermissionChange(permission, activeGroupPermissionList as _Permission[], event.target.checked);
        setActiveGroupPermissionList(updatedPermissions ?? []);
    };

    // Delete permission
    const onDeletePermission = (permissionId: string) => {
        deleteOrgPermission(
            deletePermissionMutation,
            { id: permissionId },
            (data: any) => {
                if (activeGroupPermission?.id === permissionId) {
                    setActiveGroupPermission(null);
                }

                setToastMessage({
                    message: data?.message ?? '',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                orgPermissionGroupListQuery.refetch();
            },
            (error: string, variant: string) => {
                setToastMessage({
                    message: error,
                    variant,
                    show: true,
                });
            }
        );
    };

    // on update permission
    const onUpdatePermission = () => {
        if (!updatePermissionForm) return;

        updateOrgPermission(
            updatePermissionMutation,
            updatePermissionForm,
            (data: any) => {
                setToastMessage({
                    message: data?.message ?? '',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                orgPermissionGroupListQuery.refetch();
            },
            (error: string, variant: string) => {
                setToastMessage({
                    message: error,
                    variant,
                    show: true,
                });
            }
        );
    };

    useEffect(() => {
        if (!adminOrgPermissionList || !activeGroupPermission) return;

        const currentActivePermissionGroup = adminGroupPermissionList.find((permission) => permission.id === activeGroupPermission.id);
        if (currentActivePermissionGroup) {
            const updatedPermissionList = updateOrgFullPermissions(adminOrgPermissionList, currentActivePermissionGroup?.permissions ?? []);
            setActiveGroupPermissionList(updatedPermissionList);
        }
    }, [adminGroupPermissionList, adminOrgPermissionList, activeGroupPermission, resetKey]);

    useEffect(() => {
        if (activeGroupPermissionList && activeGroupPermission) {
            const rolePermissions: _RolePermissions[] = convertToRolePermissions(activeGroupPermissionList);
            const updatedPermissionForm: _UpdatePermissionForm = {
                id: activeGroupPermission.id,
                permissionRoleName: activeGroupPermission.permissionRoleName ?? '',
                permissionRoleDescription: activeGroupPermission.permissionRoleDescription ?? '',
                rolePermissions,
            };
            setUpdatePermissionForm(updatedPermissionForm);
        }
    }, [activeGroupPermissionList, activeGroupPermission]);

    return (
        <>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <ConfirmPopup className="mlx-confirm-popup" />
            <AddOrgAdminPermission
                setToastMessage={setToastMessage}
                visible={addAdminOrgPermissionModel}
                setVisible={setAddAdminOrgPermissionModel}
                setActiveGroupPermission={setActiveGroupPermission}
            />
            <div className="page-section">
                <span className="page-header">Manage Permissions</span>
                <div className="page-content">
                    <div className="content-body">
                        <div className="row py-3 h-100 m-0">
                            <div className={`col-md-5 col-lg-4 ${screenWidth < MOBILE_BREAKPOINT ? 'mb-3' : 'h-100'} pe-0`}>
                                <div className="org-permission-body p-3 overflow-auto">
                                    <span>
                                        <div className="d-flex align-items-center mb-2 py-2">
                                            <div className="flex-grow-1">
                                                <h6 className="d-flex align-items-center card-title mb-0">
                                                    <b>{translatedText?.DEFAULT_PERMISSIONS}</b>
                                                </h6>
                                            </div>
                                            <div className=" flex-grow-0"></div>
                                        </div>
                                        <ul className="roles-link">
                                            {adminGroupPermissionList?.map((permissionGroup) => {
                                                if (!permissionGroup?.isDefault) return null;

                                                return (
                                                    <li
                                                        key={permissionGroup?.id}
                                                        className={permissionGroup?.id === activeGroupPermission?.id ? 'active' : ''}
                                                        role="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();

                                                            if (permissionGroup?.id === activeGroupPermission?.id) return;

                                                            setActiveGroupPermission(permissionGroup);
                                                        }}
                                                    >
                                                        {permissionGroup?.permissionRoleName}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </span>

                                    <span>
                                        <div className="d-flex align-items-center mt-5 mb-2 py-2">
                                            <div className="flex-grow-1">
                                                <h6 className="d-flex align-items-center card-title mb-0">
                                                    <b>{translatedText?.CUSTOM_PERMISSIONS}</b>
                                                </h6>
                                            </div>
                                            {(userType === 'organizationAdmin' ||
                                                checkActionSubPermission(
                                                    queryClient,
                                                    OrgPermissionKeys.MANAGE_PERMISSION,
                                                    OrgSubPermissionKeys.ADD_PERMISSION
                                                )) && (
                                                <div className=" flex-grow-0">
                                                    <MlxButton
                                                        size="small"
                                                        outlined
                                                        label={translatedText?.ADD}
                                                        icon="ri-add-line"
                                                        iconPos="right"
                                                        onClick={() => setAddAdminOrgPermissionModel(true)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <ul className="roles-link">
                                            {adminGroupPermissionList?.map((permissionGroup) => {
                                                if (permissionGroup?.isDefault) return null;

                                                return (
                                                    <li
                                                        key={permissionGroup?.id}
                                                        className={`d-flex flex-row align-items-center position-relative ${
                                                            permissionGroup?.id === activeGroupPermission?.id ? 'active' : ''
                                                        }`}
                                                        role="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();

                                                            if (permissionGroup?.id === activeGroupPermission?.id) return;

                                                            setActiveGroupPermission(permissionGroup);
                                                        }}
                                                    >
                                                        <span>{permissionGroup?.permissionRoleName ?? ''}</span>
                                                        {(userType === 'organizationAdmin' ||
                                                            checkActionSubPermission(
                                                                queryClient,
                                                                OrgPermissionKeys.MANAGE_PERMISSION,
                                                                OrgSubPermissionKeys.DELETE_PERMISSION
                                                            )) && (
                                                            <MlxActionIcon
                                                                className={`position-absolute ${
                                                                    permissionGroup.id === activeGroupPermission?.id ? 'text-light' : ''
                                                                }`}
                                                                actionType="DELETE"
                                                                role="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    confirmPopup({
                                                                        target: e.currentTarget,
                                                                        message: (
                                                                            <JTranslation
                                                                                type="html"
                                                                                text={DELETE_PERMISSION_MSG}
                                                                                typeCase="capitalize"
                                                                            />
                                                                        ),
                                                                        icon: 'pi pi-info-circle',
                                                                        defaultFocus: 'reject',
                                                                        accept: () => onDeletePermission(permissionGroup?.id),
                                                                        reject: () => {},
                                                                    });
                                                                }}
                                                                style={{ right: '0.5rem', zIndex: '1' }}
                                                            />
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </span>
                                </div>
                            </div>

                            <div className={`col-md-7 col-lg-8 ${screenWidth < MOBILE_BREAKPOINT ? '' : 'h-100'} ps-0`}>
                                <div className="org-permission-body mb-5 d-flex flex-column position-relative">
                                    <div className="d-flex flex-column overflow-auto">
                                        <div className="p-3 mb-1 d-flex flex-column">
                                            <InputText
                                                className="custom-input-text"
                                                id="username"
                                                aria-describedby="username-help"
                                                value={activeGroupPermission?.permissionRoleName ?? ''}
                                                placeholder={translatedText.PERMISSION_NAME}
                                                onChange={(e) => {
                                                    setActiveGroupPermission((activeGroupPermission) => {
                                                        if (!activeGroupPermission) return null;
                                                        return { ...activeGroupPermission, permissionRoleName: e.target.value };
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div className="p-3 mb-1 d-flex flex-column">
                                            <InputTextarea
                                                rows={4}
                                                value={activeGroupPermission?.permissionRoleDescription ?? ''}
                                                placeholder={translatedText.PERMISSION_DESC}
                                                onChange={(e) => {
                                                    setActiveGroupPermission((activeGroupPermission) => {
                                                        if (!activeGroupPermission) return null;
                                                        return { ...activeGroupPermission, permissionRoleDescription: e.target.value };
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div className="p-3 mt-2">
                                            <Accordion multiple={true}>
                                                {activeGroupPermissionList?.map((feature, index) => (
                                                    <AccordionTab
                                                        key={feature?.permissionKey}
                                                        data-testid={feature?.permissionKey + '#permission'}
                                                        header={
                                                            <div className={`d-flex align-items-center permission-org-accordion-tab`}>
                                                                <span className="form-check form-switch ">
                                                                    <input
                                                                        key={feature?.permissionKey}
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        autoComplete="off"
                                                                        checked={feature?.isActive}
                                                                        // disabled={feature?.isDefault}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onChange={(e) => onPermissionChangeEvent(e, feature)}
                                                                    />
                                                                </span>
                                                                <span className="permission-name text-black">
                                                                    {feature?.permissionName}
                                                                </span>
                                                            </div>
                                                        }
                                                        headerClassName={feature?.subPermissions.length > 0 ? '' : 'accordion-no-children'}
                                                        contentClassName={feature?.subPermissions.length > 0 ? '' : 'd-none'}
                                                    >
                                                        {feature?.subPermissions.length > 0 && (
                                                            <div>
                                                                {feature?.subPermissions.map((permission, _index) => (
                                                                    <div key={`${permission?.permissionKey}`} className="d-flex mb-3">
                                                                        <div className="flex-grow-0 ">
                                                                            <div className="">
                                                                                <span className="form-check form-switch">
                                                                                    <input
                                                                                        className="form-check-input"
                                                                                        type="checkbox"
                                                                                        autoComplete="off"
                                                                                        checked={permission?.isActive}
                                                                                        // disabled={permission?.isDefault}
                                                                                        onChange={(e) =>
                                                                                            onPermissionChangeEvent(e, permission)
                                                                                        }
                                                                                    />
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex-grow-1">{permission.permissionName}</div>
                                                                        <div className=""></div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </AccordionTab>
                                                ))}
                                            </Accordion>
                                        </div>
                                    </div>
                                    {(userType === 'organizationAdmin' ||
                                        checkActionSubPermission(
                                            queryClient,
                                            OrgPermissionKeys.MANAGE_PERMISSION,
                                            OrgSubPermissionKeys.ADD_PERMISSION
                                        ) ||
                                        checkActionSubPermission(
                                            queryClient,
                                            OrgPermissionKeys.MANAGE_PERMISSION,
                                            OrgSubPermissionKeys.DELETE_PERMISSION
                                        )) && (
                                        <div className="permission-footer">
                                            <MlxButton
                                                label={translatedText.CANCEL}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setResetKey((prev) => prev + 1);
                                                }}
                                                icon="ri-close-large-line"
                                                iconPos="right"
                                                size="small"
                                                outlined
                                            />
                                            <MlxButton
                                                className="ms-2"
                                                label={translatedText.SAVE}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    onUpdatePermission();
                                                }}
                                                icon="ri-check-line"
                                                iconPos="right"
                                                size="small"
                                                disabled={updatePermissionMutation?.isLoading}
                                                loading={updatePermissionMutation?.isLoading}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default withSidebar(OrgStaffPermission);
